// Customer Management data layer (Phase 2).
//
// Talks to the relational customer schema from supabase/migrations/0003_customers.sql:
//   customers · customer_contacts · customer_addresses · customer_timeline_events
//
// SECURITY MODEL (enforced server-side, mirrored here for clarity):
//   • We NEVER send company_id. A BEFORE INSERT trigger stamps it from the
//     caller's membership; RLS rejects anything out of company scope. So the
//     frontend literally cannot assign or forge a company_id.
//   • customer_number is assigned by the DB (per-company sequential).
//
// This layer is intentionally independent of the generic lib/db.js doc-store
// because customers have promoted columns + child tables.
import { supabase } from "./supabaseClient";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

// Whitelist of columns the client is allowed to write. company_id / customer_number
// are deliberately absent — the DB owns them.
const CUSTOMER_FIELDS = [
  "company_name", "trading_name", "customer_type", "status", "vat_number",
  "company_reg_number", "website", "industry", "notes", "payment_terms",
  "credit_limit", "preferred_currency", "vat_treatment", "customer_reference",
];
const CONTACT_FIELDS = ["first_name", "last_name", "job_title", "email", "mobile", "telephone", "is_primary"];
const ADDRESS_FIELDS = ["address_type", "label", "line1", "line2", "city", "county", "postal_code", "country", "is_default"];

const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};

// ── Reads ────────────────────────────────────────────────────────────────────

// List customers (RLS scopes to the caller's company). Embeds contacts + addresses
// so the list can show the primary contact and a location without N+1 queries.
// `filters`: { search, status, type }
export async function listCustomers(filters = {}) {
  let q = client()
    .from("customers")
    .select(`
      id, customer_number, company_name, trading_name, customer_type, status,
      vat_number, industry, payment_terms, credit_limit, preferred_currency,
      customer_reference, created_at,
      customer_contacts ( id, first_name, last_name, job_title, email, mobile, telephone, is_primary ),
      customer_addresses ( id, address_type, label, line1, line2, city, county, postal_code, country, is_default )
    `)
    .order("created_at", { ascending: false });

  if (filters.status && filters.status !== "all") q = q.eq("status", filters.status);
  if (filters.type && filters.type !== "all") q = q.eq("customer_type", filters.type);

  const { data, error } = await q;
  if (error) throw error;
  let rows = data || [];

  // Free-text search across the most useful fields (client-side; see Phase 3
  // recommendation about server-side full-text search for large datasets).
  const term = (filters.search || "").trim().toLowerCase();
  if (term) {
    rows = rows.filter((c) => {
      const contacts = (c.customer_contacts || [])
        .map((p) => `${p.first_name || ""} ${p.last_name || ""} ${p.email || ""}`)
        .join(" ");
      return `${c.company_name || ""} ${c.trading_name || ""} ${c.customer_number || ""} ${c.vat_number || ""} ${c.customer_reference || ""} ${contacts}`
        .toLowerCase()
        .includes(term);
    });
  }
  return rows;
}

// Full customer with children + timeline (newest first).
export async function getCustomer(id) {
  const { data, error } = await client()
    .from("customers")
    .select(`
      *,
      customer_contacts ( * ),
      customer_addresses ( * ),
      customer_timeline_events ( * )
    `)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  if (data?.customer_timeline_events) {
    data.customer_timeline_events.sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at));
  }
  return data;
}

// ── Writes ───────────────────────────────────────────────────────────────────

// Create a customer plus its contacts and addresses. Returns the new id.
export async function createCustomer(payload) {
  const c = client();
  const { data, error } = await c
    .from("customers")
    .insert(pick(payload, CUSTOMER_FIELDS)) // no company_id / customer_number — DB assigns
    .select("id")
    .single();
  if (error) throw error;
  const id = data.id;
  await replaceChildren(id, payload.contacts, payload.addresses);
  return id;
}

// Update a customer and replace its contact/address sets.
export async function updateCustomer(id, payload) {
  const c = client();
  const { error } = await c.from("customers").update(pick(payload, CUSTOMER_FIELDS)).eq("id", id);
  if (error) throw error;
  await replaceChildren(id, payload.contacts, payload.addresses);
  return id;
}

// Replace-on-save for child collections: simplest correct strategy for the
// volumes involved (a customer has a handful of contacts/addresses). We delete
// the existing set and insert the provided one. company_id is set by trigger.
async function replaceChildren(customerId, contacts, addresses) {
  const c = client();

  if (Array.isArray(contacts)) {
    const { error: delErr } = await c.from("customer_contacts").delete().eq("customer_id", customerId);
    if (delErr) throw delErr;
    const rows = contacts
      .filter((p) => (p.first_name || p.last_name || p.email || p.mobile || p.telephone))
      .map((p) => ({ ...pick(p, CONTACT_FIELDS), customer_id: customerId }));
    // Guard: never persist two primaries (DB also enforces via partial unique index).
    let primarySeen = false;
    for (const r of rows) {
      if (r.is_primary && !primarySeen) primarySeen = true;
      else r.is_primary = false;
    }
    if (rows.length && !primarySeen) rows[0].is_primary = true; // default first as primary
    if (rows.length) {
      const { error } = await c.from("customer_contacts").insert(rows);
      if (error) throw error;
    }
  }

  if (Array.isArray(addresses)) {
    const { error: delErr } = await c.from("customer_addresses").delete().eq("customer_id", customerId);
    if (delErr) throw delErr;
    const rows = addresses
      .filter((a) => (a.line1 || a.city || a.postal_code))
      .map((a) => ({ ...pick(a, ADDRESS_FIELDS), customer_id: customerId }));
    // One default per address_type (DB enforces too).
    const defaultByType = {};
    for (const r of rows) {
      if (r.is_default && !defaultByType[r.address_type]) defaultByType[r.address_type] = true;
      else r.is_default = false;
    }
    if (rows.length) {
      const { error } = await c.from("customer_addresses").insert(rows);
      if (error) throw error;
    }
  }
}

export async function deleteCustomer(id) {
  const { error } = await client().from("customers").delete().eq("id", id); // cascades children
  if (error) throw error;
}

// Timeline write helper — wired by future modules (orders/invoices/etc.). No UI yet.
export async function addTimelineEvent(customerId, event) {
  const { error } = await client()
    .from("customer_timeline_events")
    .insert({
      customer_id: customerId,
      event_type: event.event_type,
      title: event.title || null,
      body: event.body || null,
      ref_table: event.ref_table || null,
      ref_id: event.ref_id || null,
      metadata: event.metadata || {},
      occurred_at: event.occurred_at || new Date().toISOString(),
    });
  if (error) throw error;
}

// ── Option vocabularies (shared by the UI) ───────────────────────────────────
export const CUSTOMER_TYPES = [
  { value: "business", label: "Business" },
  { value: "individual", label: "Individual" },
  { value: "wholesale", label: "Wholesale" },
  { value: "retail", label: "Retail" },
  { value: "distributor", label: "Distributor" },
  { value: "government", label: "Government / Public" },
];
export const CUSTOMER_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
export const PAYMENT_TERMS = [
  { value: "due_on_receipt", label: "Due on receipt" },
  { value: "net_7", label: "Net 7 days" },
  { value: "net_14", label: "Net 14 days" },
  { value: "net_30", label: "Net 30 days" },
  { value: "net_60", label: "Net 60 days" },
  { value: "net_90", label: "Net 90 days" },
];
export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];
export const VAT_TREATMENTS = [
  { value: "standard", label: "Standard (domestic)" },
  { value: "reverse_charge", label: "Reverse charge (Intra-EU)" },
  { value: "export", label: "Export (outside EU)" },
  { value: "exempt", label: "Exempt" },
  { value: "zero_rated", label: "Zero-rated" },
];
export const ADDRESS_TYPES = [
  { value: "registered_office", label: "Registered Office" },
  { value: "billing", label: "Billing" },
  { value: "delivery", label: "Delivery" },
];
